import type {
  AssetType,
  DeviceSlot,
  ExperimentOutcomeSummary,
  ExperimentResource,
  OutcomeVerdict,
  Target,
} from '@studio/shared';

/** How many closed Experiments one page of the history holds. */
export const HISTORY_PAGE = 12;

/**
 * `notRecorded` is a fourth answer, not the absence of one.
 *
 * A closed Experiment whose Outcome was never written is exactly what an operator goes
 * looking for, so it has to be selectable rather than only reachable by clearing the
 * filter and reading every card.
 */
export type OutcomeFilter = OutcomeVerdict | 'all' | 'notRecorded';

export interface HistoryFilter {
  readonly query: string;
  readonly assetType: AssetType | 'all';
  readonly target: string;
  readonly outcome: OutcomeFilter;
}

export const NO_FILTER: HistoryFilter = { query: '', assetType: 'all', target: 'all', outcome: 'all' };

/** The four verdicts in the operator's words. One vocabulary, wherever a verdict shows. */
export const OUTCOME_LABELS: Readonly<Record<OutcomeVerdict, string>> = {
  candidateWon: 'Candidate победил',
  controlWon: 'Control победил',
  noClearWinner: 'Нет явного победителя',
  notCompleted: 'Не запускался / остановлен',
};

export function outcomeLabel(verdict: OutcomeVerdict): string {
  return OUTCOME_LABELS[verdict];
}

/** What a history card says about how it went. Never a number that was not measured. */
export function outcomeLine(outcome: ExperimentOutcomeSummary | null): string {
  if (outcome === null) return 'Outcome ещё не записан';
  const uplift =
    outcome.uplift === null ? '' : ` · ${outcome.uplift.label} ${outcome.uplift.value}${outcome.uplift.unit ?? ''}`;
  return `${outcomeLabel(outcome.verdict)}${uplift} · ${outcome.resultDate.slice(0, 10)}`;
}

/** One stable key per Target, so `Brazil · pt-BR` and Global can both be filtered on. */
export function targetKey(target: Target): string {
  return target.kind === 'global' ? 'global' : `${target.market}·${target.locale}`;
}

export function targetLabel(target: Target): string {
  return target.kind === 'global' ? 'Global' : `${target.market} · ${target.locale}`;
}

export function assetTypeLabel(assetType: AssetType): string {
  switch (assetType) {
    case 'appIcon':
      return 'App icon';
    case 'featureGraphic':
      return 'Feature graphic';
    case 'screenshotSet':
      return 'Screenshot set';
    case 'previewVideo':
      return 'Preview video';
  }
}

/**
 * The Google Play screenshot slot, in the operator's words. The slot is always
 * explicit and phone is the ordinary default; it is never inferred from dimensions.
 */
export const DEVICE_SLOT_LABELS: Readonly<Record<DeviceSlot, string>> = {
  phone: 'Телефон',
  sevenInchTablet: 'Планшет 7″',
  tenInchTablet: 'Планшет 10″',
  chromebook: 'Chromebook',
  androidTv: 'Android TV',
  wearOs: 'Wear OS',
  xr: 'Android XR',
};

export function deviceSlotLabel(slot: DeviceSlot): string {
  return DEVICE_SLOT_LABELS[slot];
}

/** Newest first: the history is read as a timeline, not as a table. */
export function chronological(experiments: readonly ExperimentResource[]): readonly ExperimentResource[] {
  return [...experiments].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function matches(experiment: ExperimentResource, filter: HistoryFilter): boolean {
  if (filter.assetType !== 'all' && experiment.assetType !== filter.assetType) return false;
  if (filter.target !== 'all' && targetKey(experiment.target) !== filter.target) return false;
  if (filter.outcome === 'notRecorded' && experiment.outcome !== null) return false;
  if (filter.outcome !== 'all' && filter.outcome !== 'notRecorded' && experiment.outcome?.verdict !== filter.outcome) {
    return false;
  }
  const query = filter.query.trim().toLowerCase();
  if (query === '') return true;
  return `${experiment.hypothesis ?? ''} ${experiment.brief}`.toLowerCase().includes(query);
}

/**
 * The cursor. A closed history grows without bound, so the page holds what has been
 * asked for and nothing else: a hundred closed Experiments render twelve.
 */
export function pageOf<T>(items: readonly T[], pages: number, size: number = HISTORY_PAGE): readonly T[] {
  return items.slice(0, Math.max(1, pages) * size);
}
